import { useState } from 'react';
import { Search, History, Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { RoleDto, PermissionsByModule } from '../types';
import { useAuthorizationStore } from '../store';

interface PermissionsPanelProps {
  role: RoleDto;
  permissionsByModule: PermissionsByModule[];
  onOpenHistory: () => void;
  readOnly?: boolean;
}

export function PermissionsPanel({
  role,
  permissionsByModule,
  onOpenHistory,
  readOnly = false,
}: PermissionsPanelProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const {
    pendingPermissionChanges,
    togglePermission,
    discardPermissionChanges,
    savePermissionChanges,
    isSavingPermissions,
  } = useAuthorizationStore();

  // 86d3d1z40: derive from the directly-subscribed `pendingPermissionChanges` (a dep the
  // React Compiler can track) instead of calling the store's `hasPendingChanges()` in JSX
  // — that getter reads state via get() internally, which the compiler can't see, so it
  // memoized the footer and left Save/Cancel stuck disabled after a toggle.
  const hasChanges = Object.keys(pendingPermissionChanges).length > 0;

  // Filter modules and permissions based on search
  const filteredModules = permissionsByModule
    .map((module) => ({
      ...module,
      permissions: module.permissions.filter(
        (p) =>
          p.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
          p.permissionCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (p.description && p.description.toLowerCase().includes(searchTerm.toLowerCase()))
      ),
    }))
    .filter((module) => module.permissions.length > 0);

  // Check if a permission is granted (considering pending changes)
  const isPermissionGranted = (permissionCode: string): boolean => {
    if (pendingPermissionChanges[permissionCode] !== undefined) {
      return pendingPermissionChanges[permissionCode];
    }
    return role.permissions.includes(permissionCode);
  };

  // Check if a permission has pending changes
  const hasChange = (permissionCode: string): boolean => {
    return pendingPermissionChanges[permissionCode] !== undefined;
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold">Role: {role.name}</h3>
            <p className="text-xs text-muted-foreground">
              <code>{role.roleCode}</code>
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={onOpenHistory}>
            <History className="h-4 w-4 mr-2" />
            History
          </Button>
        </div>

        {readOnly && (
          <Alert>
            <ShieldCheck className="h-4 w-4" />
            <AlertDescription>
              System Administrator role permissions are managed by the system seeder and cannot be modified here.
            </AlertDescription>
          </Alert>
        )}

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search permissions..."
            className="pl-8"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Permissions Accordion */}
      <div className="flex-1 overflow-y-auto p-4">
        <TooltipProvider>
          <Accordion
            type="multiple"
            defaultValue={permissionsByModule.map((m) => m.moduleCode)}
            className="space-y-2"
          >
            {filteredModules.map((module) => (
              <AccordionItem
                key={module.moduleCode}
                value={module.moduleCode}
                className="border rounded-lg px-4"
              >
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{module.moduleName}</span>
                    <span className="text-xs text-muted-foreground">
                      ({module.permissions.length})
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3 pt-2">
                    {module.permissions.map((permission) => (
                      <div
                        key={permission.permissionCode}
                        className="flex items-start gap-3"
                      >
                        <Checkbox
                          id={permission.permissionCode}
                          checked={isPermissionGranted(permission.permissionCode)}
                          onCheckedChange={() =>
                            !readOnly && togglePermission(permission.permissionCode)
                          }
                          disabled={readOnly}
                          className="mt-0.5"
                        />
                        <div className="flex-1 min-w-0">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <label
                                htmlFor={permission.permissionCode}
                                className={`text-sm ${readOnly ? 'cursor-default text-muted-foreground' : 'cursor-pointer'} ${
                                  hasChange(permission.permissionCode)
                                    ? 'font-medium text-primary'
                                    : ''
                                }`}
                              >
                                {permission.action}
                                {hasChange(permission.permissionCode) && (
                                  <span className="ml-1 text-xs text-warning">
                                    (modified)
                                  </span>
                                )}
                              </label>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-xs">
                              <div className="space-y-1">
                                <div className="font-mono text-xs">
                                  {permission.permissionCode}
                                </div>
                                {permission.description && (
                                  <div className="text-xs">
                                    {permission.description}
                                  </div>
                                )}
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </TooltipProvider>

        {filteredModules.length === 0 && (
          <div className="text-center text-muted-foreground text-sm py-8">
            No permissions match your search
          </div>
        )}
      </div>

      {/* Footer with actions */}
      <div className="p-4 border-t flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          {!readOnly && hasChanges ? (
            <span className="text-warning">You have unsaved changes</span>
          ) : (
            <span>{role.permissions.length} permissions assigned</span>
          )}
        </div>
        {!readOnly && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={discardPermissionChanges}
              disabled={!hasChanges || isSavingPermissions}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={!hasChanges || isSavingPermissions}
              onClick={async () => {
                const success = await savePermissionChanges();
                if (success) {
                  toast.success('Permissions updated successfully');
                } else {
                  toast.error('Failed to update permissions');
                }
              }}
            >
              {isSavingPermissions && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
